import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Radio, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { MdDelete, MdEdit } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { SiMinutemailer } from "react-icons/si";
import Searchbar from "../../common/searchbar";
import TablePagination from "../../common/tablePagination";
import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";
import { itemsPerPage } from "../../utils/constants";
import { deleteTemplateApi, getSystemVariablesApi, listTemplatesApi, setTemplateActiveApi, errorMessage } from "./api";
import { EmailTable, formatDateTime, useFeedback } from "./common";
import PreviewDialog from "./PreviewDialog";
import TestMailDialog from "./TestMailDialog";

const cellText = (text, width = 220) => (
  <Typography variant="body2" title={text || ""} sx={{ maxWidth: width, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
    {text || "—"}
  </Typography>
);

// Notification template master: one row per event with its SMS, WhatsApp and Email.
export default function TemplateList() {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const project = Cookies.get("mhet_mst_project");
  const [templates, setTemplates] = useState([]);
  const [systemVariables, setSystemVariables] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState(null);
  const [test, setTest] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    feedback.loader(true);
    try {
      setTemplates((await listTemplatesApi()) || []);
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not load templates"));
    }
    feedback.loader(false);
  };

  useEffect(() => {
    load();
    getSystemVariablesApi().then((v) => setSystemVariables(v || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return templates.filter((t) => !term || [t.eventname, t.code, t.sms?.dltid, t.whatsapp?.templatename, t.email?.subject].some((v) => v?.toLowerCase().includes(term)));
  }, [templates, search]);
  const pageCount = Math.ceil(filtered.length / itemsPerPage);
  const rows = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const setActive = async (t, isactive) => {
    if (t.isactive === isactive) return;
    feedback.loader(true);
    try {
      await setTemplateActiveApi(t.templateid, isactive);
      feedback.success(isactive ? "Template enabled" : "Template disabled");
      await load();
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not change the status"));
    }
    feedback.loader(false);
  };

  const remove = async () => {
    const t = toDelete;
    setToDelete(null);
    feedback.loader(true);
    try {
      await deleteTemplateApi(t.templateid);
      feedback.success(`"${t.eventname}" deleted`);
      await load();
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not delete the template"));
    }
    feedback.loader(false);
  };

  const columns = [
    { name: "S.No", render: (_, i) => i + 1 },
    { name: "Event", align: "left", render: (t) => <><b>{t.eventname}</b><Typography variant="caption" sx={{ display: "block", color: "#777" }}>{t.code} · v{t.version}</Typography></> },
    { name: "SMS (DLT ID)", render: (t) => t.sms?.dltid || "—" },
    { name: "WhatsApp Template", render: (t) => t.whatsapp?.templatename || "—" },
    { name: "Email Subject", align: "left", render: (t) => cellText(t.email?.subject) },
    { name: "Last Updated", render: (t) => formatDateTime(t.updateddate || t.createddate) },
    { name: "Enable", render: (t) => <Radio checked={t.isactive === true} onClick={() => setActive(t, true)} /> },
    { name: "Disable", render: (t) => <Radio checked={t.isactive === false} onClick={() => setActive(t, false)} /> },
    {
      name: "Action",
      render: (t) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/${project}/notification-templates?page=edit&id=${t.templateid}`)}><MdEdit size={20} /></IconButton></Tooltip>
          <Tooltip title="Preview"><IconButton size="small" onClick={() => setPreview(t)}><FaEye size={18} /></IconButton></Tooltip>
          <Tooltip title="Send a test"><IconButton size="small" onClick={() => setTest(t)}><SiMinutemailer size={18} /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" onClick={() => setToDelete(t)}><MdDelete size={20} /></IconButton></Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Grid container rowSpacing={2}>
      <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 320px", maxWidth: 560 }}>
          <Searchbar value={search} handleSearch={(e) => { setSearch(e.target.value); setPage(1); }} />
        </Box>
        <MauvelousPrimaryButton title="Add Template" handleClick={() => navigate(`/${project}/notification-templates?page=create`)} />
      </Grid>
      <Grid item xs={12}>
        <EmailTable columns={columns} rows={rows} startIndex={(page - 1) * itemsPerPage} emptyText="No templates yet" />
        {pageCount > 1 && <TablePagination pageCount={pageCount} currentPage={page} handlePageChange={(e, v) => setPage(v)} />}
      </Grid>
      {preview && <PreviewDialog template={preview} systemVariables={systemVariables} onClose={() => setPreview(null)} />}
      {test && <TestMailDialog template={test} systemVariables={systemVariables} onClose={() => setTest(null)} />}
      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <Typography>Delete <b>{toDelete?.eventname}</b> ({toDelete?.code})?</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>It can no longer be sent. Messages already sent stay in Send History.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setToDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" size="small" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
